import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";

import { createAppTheme } from "./theme";
import { BrandProvider } from "./brand/BrandContext";
import { resolveBrandFromStorefrontConfig } from "./brand/resolveBrand";
import { setRuntimeBrand } from "./brand/runtime";
import { fetchStorefrontConfig } from "./api/storefront";

import PrivateRoute from "./components/PrivateRoute";
import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";
import ScrollToTop from "./utils/ScrollToTop";

import HomePage from "./pages/HomePage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentStatus from "./pages/PaymentStatusPage";
import BookingDetails from "./pages/BookingDetailsPage";
import GoogleSuccessPage from "./pages/GoogleSuccessPage";

import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";

import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsAndConditions from "./pages/TermsAndConditionsPage";
import PublicOfferPage from "./pages/PublicOfferPage";

import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import TenantsListPage from "./pages/admin/TenantsListPage";
import TenantDetailPage, {
  MyOrganizationRedirect,
} from "./pages/admin/TenantDetailPage";
import BusQrPage from "./pages/BusQrPage";
import AboutUsPage from "./pages/AboutUsPage";
import ProfilePage from "./pages/ProfilePage";
import ForbiddenPage from "./pages/ForbiddenPage";

import DashboardPage from "./pages/admin/DashboardPage";
import BookingsPage from "./pages/admin/BookingsPage";
import AdminBookingDetails from "./pages/admin/BookingDetailsPage";
import AdminNewBooking from "./pages/admin/NewBookingPage";
import TripPassengersPage from "./pages/admin/TripPassengersPage";
import AgentReconciliationPage from "./pages/admin/AgentReconciliationPage";
import DriverCashReconciliationPage from "./pages/admin/DriverCashReconciliationPage";
import LoadFactorPage from "./pages/admin/LoadFactorPage";
import RoutesListPage from "./pages/admin/RoutesListPage";
import RouteEditPage from "./pages/admin/RouteEditPage";
import CustomerPackagesPage from "./pages/admin/CustomerPackages";
import DriverExpensesPage from "./pages/admin/DriverExpensesPage";

import CheckTicket from "./pages/CheckTicket";
import NotFoundPage from "./pages/NotFoundPage";
import AgentDashboardPage from "./pages/agent/AgentDashboardPage";
import AgentBookingsPage from "./pages/agent/AgentBookingsPage";
import AgentSelfReconciliationPage from "./pages/agent/AgentSelfReconciliationPage";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ukUA, enUS } from "@mui/x-date-pickers/locales";
import { SnackbarProvider } from "notistack";
import { useTranslation } from "react-i18next";

const MUI_LOCALE_MAP = {
  uk: { adapter: "uk", localeText: ukUA.components.MuiLocalizationProvider.defaultProps.localeText },
  en: { adapter: "en", localeText: enUS.components.MuiLocalizationProvider.defaultProps.localeText },
};

function AppContent() {
  const { i18n } = useTranslation();
  const muiLocale = MUI_LOCALE_MAP[i18n.language] || MUI_LOCALE_MAP.uk;
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={muiLocale.adapter}
      localeText={muiLocale.localeText}
    >
      <Router>
        <ScrollToTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/payment-status" element={<PaymentStatus />} />
            <Route path="/booking/:id" element={<BookingDetails />} />
            <Route path="/google-success" element={<GoogleSuccessPage />} />
            <Route path="/registration" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route element={<PrivateRoute />}>
              <Route path="/profile" element={<ProfilePage />} />
            </Route>
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/offer" element={<PublicOfferPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/bus" element={<BusQrPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route element={<PrivateRoute allowedGroups={["Company Admin"]} />}>
            <Route element={<AdminLayout />}>
              <Route path="/administrator/dashboard" element={<DashboardPage />} />
              <Route path="/administrator/bookings" element={<BookingsPage />} />
              <Route path="/administrator/bookings/:id" element={<AdminBookingDetails />} />
              <Route path="/administrator/bookings/new" element={<AdminNewBooking />} />
              <Route path="/administrator/reconciliation" element={<AgentReconciliationPage />} />
              <Route path="/administrator/driver-cash" element={<DriverCashReconciliationPage />} />
              <Route path="/administrator/load-factor" element={<LoadFactorPage />} />
              <Route path="/administrator/tenants" element={<TenantsListPage />} />
              <Route path="/administrator/organization" element={<MyOrganizationRedirect />} />
              <Route path="/administrator/tenants/:id" element={<TenantDetailPage />} />
              <Route path="/administrator/routes" element={<RoutesListPage />} />
              <Route path="/administrator/routes/new" element={<RouteEditPage />} />
              <Route path="/administrator/routes/:id" element={<RouteEditPage />} />
            </Route>
          </Route>
          <Route
            element={<PrivateRoute allowedGroups={["Company Admin", "Company Driver"]} />}
          >
            <Route element={<AdminLayout />}>
              <Route path="/administrator/trips/passengers" element={<TripPassengersPage />} />
              <Route path="/administrator/trips/:tripId/passengers" element={<TripPassengersPage />} />
              <Route path="/administrator/packages" element={<CustomerPackagesPage />} />
            </Route>
          </Route>
          <Route element={<PrivateRoute allowedGroups={["Company Driver"]} />}>
            <Route element={<AdminLayout />}>
              <Route path="/administrator/expenses" element={<DriverExpensesPage />} />
              <Route path="/check-ticket" element={<CheckTicket />} />
            </Route>
          </Route>
          <Route element={<PrivateRoute allowedGroups={["AGENT_MANAGER", "Agent Manager"]} />}>
            <Route element={<AdminLayout />}>
              <Route path="/agent" element={<AgentDashboardPage />} />
              <Route path="/agent/bookings" element={<AgentBookingsPage />} />
              <Route path="/agent/reconciliation" element={<AgentSelfReconciliationPage />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </LocalizationProvider>
  );
}

function StorefrontBootError({ onRetry }) {
  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box textAlign="center">
        <Typography variant="h4" color="error" gutterBottom>
          Storefront unavailable
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Could not load branding for this host. Check the domain or try again.
        </Typography>
        <Stack direction="row" justifyContent="center" spacing={2}>
          <Button variant="contained" onClick={onRetry}>
            Retry
          </Button>
        </Stack>
      </Box>
    </Container>
  );
}

function App() {
  const { t, i18n } = useTranslation("common");
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [resolvedBrand, setResolvedBrand] = useState(null);

  useEffect(() => {
    const description = t("meta.description");
    document
      .querySelectorAll(
        'meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]'
      )
      .forEach((el) => el.setAttribute("content", description));
  }, [t, i18n.language]);

  const loadConfig = React.useCallback(() => {
    setStatus("loading");
    fetchStorefrontConfig()
      .then((config) => {
        const next = resolveBrandFromStorefrontConfig(config);
        setRuntimeBrand(next);
        setResolvedBrand(next);
        document.title = next.title || next.name;
        if (next.favicon) {
          const link =
            document.querySelector("link[rel='icon']") ||
            document.createElement("link");
          link.rel = "icon";
          link.href = next.favicon;
          if (!link.parentNode) document.head.appendChild(link);
        }
        const metaTheme = document.querySelector("meta[name='theme-color']");
        if (metaTheme && next.colors?.primary) {
          metaTheme.setAttribute("content", next.colors.primary);
        }
        setStatus("ready");
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        const host = (window.location.hostname || "").toLowerCase();
        const isLocal =
          host === "localhost" || host === "127.0.0.1" || host === "";
        // Local/dev without API: keep FE-05a fallback so checkout still works.
        if (statusCode !== 404 && isLocal) {
          const next = resolveBrandFromStorefrontConfig(null);
          setRuntimeBrand(next);
          setResolvedBrand(next);
          document.title = next.title || next.name;
          setStatus("ready");
          return;
        }
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const muiTheme = useMemo(
    () => createAppTheme(resolvedBrand?.colors),
    [resolvedBrand]
  );

  if (status === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === "error" || !resolvedBrand) {
    return <StorefrontBootError onRetry={loadConfig} />;
  }

  return (
    <BrandProvider brand={resolvedBrand}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <AppContent />
        </SnackbarProvider>
      </ThemeProvider>
    </BrandProvider>
  );
}

export default App;
